import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { FieldType, FormlyMaterialModule } from '@ngx-formly/material';
import { DEFAULT_DEBOUNCE_TIME } from '@shared/constants';
import { AutocompleteOption } from 'app/core/json-schema-controls/models/autocomplete-option.model';
import { AutocompleteProps } from 'app/core/json-schema-controls/models/autocomplete-props.model';
import { JsonSchemaService } from 'app/core/json-schema-controls/services/json-schema.service';
import { Observable, of, Subject } from 'rxjs';
import { debounceTime, filter, startWith, switchMap, take, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'formly-autocomplete-type',
  standalone: true,
  imports: [
    MatAutocompleteModule,
    MatInputModule,
    FormlyMaterialModule,
    ReactiveFormsModule,
    FormlyModule,
    CommonModule,
    MatFormFieldModule,
    MatIconModule,
  ],
  templateUrl: './autocomplete-type.component.html',
  styleUrl: './autocomplete-type.component.scss',
  providers: [JsonSchemaService],
})
export class AutocompleteTypeComponent
  extends FieldType<FormlyFieldConfig<AutocompleteProps>>
  implements OnInit, OnDestroy
{
  filter: AutocompleteOption[] = [];
  private $destroy: Subject<void> = new Subject<void>();
  customValue: string = '';

  constructor(
    private jsonSchemaService: JsonSchemaService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit() {
    this.initialDataLoad();
    this.listenToValueChanges();
  }

  listenToValueChanges() {
    this.formControl.valueChanges
      .pipe(
        startWith(''),
        takeUntil(this.$destroy),
        debounceTime(DEFAULT_DEBOUNCE_TIME),
        switchMap((value: string) => {
          if (typeof value === 'string') {
            this.customValue = value;
          }

          if (
            (this.props?.gqlQuery &&
              this.props?.gqlQuery?.query &&
              this.props?.gqlQuery?.fields &&
              this.props?.gqlQuery?.fields?.label &&
              this.props?.gqlQuery?.fields?.value &&
              typeof value === 'string') ||
            typeof value === 'undefined'
          ) {
            return this.filterGraphqlData(value);
          }
          return of([]);
        })
      )
      .subscribe(data => {
        if (this.props?.allowCustomValues) {
          if (this.customValue && !this.valueExistsInOptions(this.customValue)) {
            this.addCustomValueToOptions();
          }
        }
      });
  }

  initialDataLoad(): void {
    if (
      this.props?.gqlQuery &&
      this.props?.gqlQuery?.query &&
      this.props?.gqlQuery?.fields &&
      this.props?.gqlQuery?.fields?.label &&
      this.props?.gqlQuery?.fields?.value
    ) {
      if (this.props.gqlQuery.searchable) {
        if (this.props.gqlQuery?.queryInput) {
          const queryInput = this.props.gqlQuery.queryInput;
          const variables = this.props.gqlQuery.variables as Record<string, Record<string, unknown>>;
          variables[queryInput]['searchKey'] = '';
        } else {
          const variables = this.props.gqlQuery.variables as Record<string, unknown>;
          variables['searchKey'] = '';
        }
      }
      this.loadDataWithGql(this.props as unknown as Record<string, unknown>)
        .pipe(
          take(1),
          tap(result => {
            const savedValue = result.find(item => item.value === this.field.formControl.value);
            const derrivedProps = {
              gqlQuery: { ...this.props.gqlQuery },
              variables: { ...this.props.gqlQuery.variables },
            };
            if (savedValue) {
              this.field.formControl.setValue(savedValue);
            } else {
              if (this.props.gqlQuery.singleQuery?.query) {
                derrivedProps.gqlQuery.query = this.props.gqlQuery.singleQuery.query;
                derrivedProps.gqlQuery.variables = {
                  ...this.props.gqlQuery.singleQuery.variables,
                  [Object.keys(this.props.gqlQuery.singleQuery.variables)[0]]: this.field.formControl.value,
                };

                Object.entries(derrivedProps?.gqlQuery?.variables).forEach(([_, value]) => {
                  if (value === undefined) {
                    return;
                  }
                });

                if (Object.values(derrivedProps?.gqlQuery?.variables).every(value => value !== undefined)) {
                  this.jsonSchemaService
                    .singleItemGraphqlQuery(derrivedProps)
                    .pipe(
                      filter((data: AutocompleteOption[]) => !!data),
                      take(1),
                      tap(res => {
                        this.field.formControl.setValue(res);
                      })
                    )
                    .subscribe();
                }
              }
            }
          })
        )
        .subscribe();
    }
  }

  filterGraphqlData(filterValue: string | undefined): Observable<AutocompleteOption[]> {
    const derrivedProps = {
      gqlQuery: { ...this.props.gqlQuery },
      variables: this.props.gqlQuery.variables,
    };
    if (this.props.gqlQuery.searchable) {
      if (typeof filterValue === 'string') {
        if (this.props.gqlQuery?.queryInput) {
          const queryInput = this.props.gqlQuery.queryInput;
          const inputVariables = derrivedProps.gqlQuery.variables[queryInput] as Record<string, unknown>;

          derrivedProps.gqlQuery.variables[queryInput] = {
            ...inputVariables,
            searchKey: filterValue,
          };
        } else {
          derrivedProps.gqlQuery.variables = {
            ...(derrivedProps.gqlQuery.variables as Record<string, unknown>),
            searchKey: filterValue,
          };
        }
      }
      return this.loadDataWithGql(derrivedProps).pipe(
        take(1),
        tap(result => {
          this.filter = result;
          this.cdr.detectChanges();
        })
      );
    }

    return this.loadDataWithGql(derrivedProps).pipe(
      take(1),
      filter((data: AutocompleteOption[]) => {
        data = this.filter = data.filter((item: AutocompleteOption) => {
          const label = item?.label?.toLowerCase();
          const searchValue = typeof filterValue === 'string' ? filterValue?.toLowerCase() : '';
          return label.includes(searchValue);
        });
        return !!data;
      })
    );
  }

  displayFn(value: AutocompleteOption | string): string {
    return typeof value === 'object' && value ? value.label : (value as string);
  }

  loadDataWithGql(props: Record<string, unknown>): Observable<AutocompleteOption[]> {
    return this.jsonSchemaService.graphqlQuery(props);
  }

  getStylesClasses(id: string): string {
    if (!this.props?.styleClasses || !this.props?.styleClasses[id]) {
      return '';
    }
    return this.props?.styleClasses[id];
  }

  override ngOnDestroy(): void {
    this.$destroy.next();
    this.$destroy.complete();
  }

  valueExistsInOptions(value: string): boolean {
    if (!value) return true;
    return this.filter.some(option => option.label?.toLowerCase() === value.toLowerCase());
  }

  addCustomValueToOptions(): void {
    if (!this.customValue) return;

    const customOptionExists = this.filter.some(
      option => option.isCustomValue && option.label.toLowerCase() === this.customValue.toLowerCase()
    );

    if (!customOptionExists) {
      const customOption: AutocompleteOption = {
        label: this.customValue,
        value: this.customValue,
        isCustomValue: true,
      };

      this.filter = [customOption, ...this.filter];
      this.cdr.detectChanges();
    }
  }

  createCustomValue(): void {
    if (!this.customValue) return;

    const customValue: AutocompleteOption = {
      label: this.customValue,
      value: this.customValue,
      isCustomValue: true,
    };

    this.field.formControl.setValue(customValue);
    this.customValue = '';
  }

  get selectFormControl(): FormControl {
    return this.formControl as unknown as FormControl;
  }
}
